import { ChangeEvent, useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { FormControlChildProps } from "../form-control";
import StandardizeContentService from "@app/services/http/standardize-content.service";

export interface TextAreaProps extends FormControlChildProps {
  value?: number | string;

  defaultValue?: string | number;

  /**
   * Default: undefined
   */
  defaultStatus?: undefined | "valid" | "inValid" | "warn";

  /**
   * Default: 200px
   */
  width?: number | string;

  /**
   * Default: 100px
   */
  height?: number | string;

  /**
   * Default: 2
   */
  rows?: number;

  className?: string;

  errorClassName?: string;

  textAreaClassName?: string;

  placeholder?: string;

  isStandardizeContent?: boolean;

  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;

  onBlur?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
}

function TextArea({
  formik,
  id = undefined,
  value = "",
  name = "",
  placeholder,
  disabled = false,
  width = 200,
  height = 100,
  rows = 2,
  className = "",
  textAreaClassName = "",
  status,
  defaultStatus = undefined,
  errorClassName = "",
  onChange,
  onBlur,
  fmOnChange,
  fmOnBlur,
  isStandardizeContent = false,
}: TextAreaProps) {
  const [txaValue, setTxaValue] = useState(value || "");
  const [_status, changeStatus] = useState(status || defaultStatus);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    changeStatus(status || defaultStatus);
  }, [defaultStatus, status]);

  const handleBlur = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onBlur && onBlur(e);

      fmOnBlur && fmOnBlur(e);
    },
    [fmOnBlur, onBlur]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange && onChange(e);

      fmOnChange && fmOnChange(e);

      setTxaValue(e.currentTarget.value);
    },
    [fmOnChange, onChange]
  );

  useEffect(() => {
    setTxaValue(value || "");
  }, [value]);

  const isError = _status === "inValid" || _status === "warn";

  const handleStandardize = async () => {
    setLoading(true);
    try {
      const res = await StandardizeContentService.getStandardizeContent(
        String(txaValue)
      );

      if (res?.data?.content) {
        setTxaValue(res.data.content);

        if (onChange) {
          const event = {
            ...({} as ChangeEvent<HTMLTextAreaElement>),
            currentTarget: { value: res.data.content } as any,
            target: { value: res.data.content } as any,
          };

          onChange(event);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${className}`} style={{ width, height }}>
      <textarea
        id={id}
        rows={rows}
        name={name}
        placeholder={placeholder}
        className={clsx(
          `w-full min-h-[80px] h-full box-border border border-solid border-[#bdbdbd] pl-2 py-2 rounded outline-none
            ${textAreaClassName}`,
          {
            "!border !border-solid !border-[#FF0000]": isError,
          }
        )}
        value={txaValue}
        disabled={disabled}
        onBlur={handleBlur}
        onChange={handleChange}
      />
      {isStandardizeContent && (
        <div className="flex">
          <button
            type="button"
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-[13px] ml-auto"
            onClick={handleStandardize}
            disabled={loading}
          >
            {loading
              ? "Đang kiểm tra..."
              : "Kiểm tra và sửa lỗi chính tả tự động"}
          </button>
        </div>
      )}
      {isError && (
        <div className={`flex error-color text-xs ${errorClassName}`}>
          {formik &&
            formik.getFieldMeta(name!).error &&
            formik.getFieldMeta(name!).error}
        </div>
      )}
    </div>
  );
}

export default TextArea;
